/**
 * Warn the user before they leave a page with unsaved work.
 *
 * - Browser tab close / reload: registers the standard beforeunload
 *   handler (browsers ignore custom strings; this is just the binary
 *   "are you sure" prompt).
 * - In-app navigation: react-router v6 has `useBlocker` but it lives
 *   in the data-router APIs we don't use; instead we monkey-patch the
 *   `<a>` click handler at the boundary the editor controls (the Back
 *   button). The hook exposes `confirmLeave()` that pages call before
 *   navigating; if dirty, it returns false unless the user confirms.
 *
 * Keep this dumb on purpose: it's a guardrail, not a state machine.
 */

import { useEffect, useRef } from 'react';

export default function useUnsavedGuard(isDirty) {
    const dirtyRef = useRef(isDirty);
    useEffect(() => { dirtyRef.current = isDirty; }, [isDirty]);

    useEffect(() => {
        const handler = (e) => {
            if (!dirtyRef.current) return undefined;
            e.preventDefault();
            // Required for older browsers; modern ones show a generic prompt
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    const confirmLeave = () => {
        if (!dirtyRef.current) return true;
        return window.confirm(
            'You have unsaved changes that may not have been written to the chart yet. Leave anyway?'
        );
    };

    return { confirmLeave };
}
